import {
  Button,
  IconButton,
  InputAdornment,
  TextField as MaterialTextField,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import { PlayCircle } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import {
  BooleanField,
  // useRecordContext,
  BooleanInput,
  Create,
  Datagrid,
  DateInput,
  DateTimeInput,
  DeleteWithConfirmButton,
  Edit,
  FormTab,
  FunctionField,
  ImageField,
  ImageInput,
  InfiniteList,
  List,
  NumberInput,
  RadioButtonGroupInput,
  TabbedForm,
  TextField,
  TextInput,
  UrlField,
  usePermissions,
  useNotify,
  useRedirect,
  useRefresh,
  useRecordContext,
} from "react-admin";

import { Shuffle } from "@mui/icons-material";
import {
  CardListActions,
  CategoryInput,
  CopyToClipboard,
  EditorToolbar,
  EventSpeakersInput,
  HostInput,
  LanguageInput,
  LocationInput,
  MorePagination,
  OrganizationInput,
  SpeakerInput,
  SubCategoryInput,
  validation,
} from "../../components";
import DateTimeField from "../../components/field/DateTimeField";
import SanitizedP from "../../components/field/SanitizedP";
import { authClient, authUser } from "../../server";
import { MediaUploader } from "../../uploader";
import useTemporalWorkflow from "../../hooks/useTemporalWorkflow";
// import { LectureRethink } from "./LectureRethink";

const styles = {
  label: {
    fontWeight: 400,
    padding: 0,
    marginTop: 16,
    marginBottom: 2,
    fontSize: 12,
  },
  url: {
    margin: 2,
  },
};

const defaultValue = {
  on_recent_list: 1,
};

const validate = {
  title: [validation.required(), validation.minLength(2)],
  date_recorded: [validation.required()],
  language: [validation.required()],
  location: [validation.required()],
  speaker: [validation.required()],
  category: [validation.required()],
};

const SendNotificationsButton = (props) => {
  const showNotification = useNotify();
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const {
      record: { id },
    } = props;
    if (!window.confirm("Are you sure?")) {
      return;
    }
    authClient
      .post("/temporal/workflows/FollowingNotifications", {
        taskQueue: "general",
        workflowId: `FollowingNotifications-${id}`,
        args: [{ lectureId: id }],
      })
      .then(() => {
        showNotification("Notifications sent.");
      })
      .catch((err) => {
        showNotification(err, { type: "error" });
      });
  };

  return <Button onClick={handleClick}>Send Notifications</Button>;
};

const failedWorkflowStatuses = [
  "CANCELLED",
  "FAILED",
  "TERMINATED",
  "TIMED_OUT",
];

/** Temporal retains the latest run under this lecture-specific workflow ID,
 * so admins can see the outcome after leaving or reloading the edit page. */
const ReUploadToHotlineButton = () => {
  const lecture = useRecordContext();
  const showNotification = useNotify();
  const [workflow, setWorkflow] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [startedHere, setStartedHere] = useState(false);
  const [statusError, setStatusError] = useState("");
  const workflowId = lecture ? `LectureVoitexUploading-${lecture.id}` : null;
  const workflowStatus = workflow?.status?.name;
  const isRunning = isStarting || workflowStatus === "RUNNING";

  const refreshStatus = useCallback(async () => {
    if (!workflowId) return;
    try {
      const response = await authClient.get(
        `/temporal/workflows/x/${workflowId}`,
      );
      setWorkflow(response.json);
      setStatusError("");
    } catch (error) {
      if (error.status === 404) {
        setWorkflow(null);
        setStatusError("");
        return;
      }
      setStatusError("Unable to check the hotline re-upload status.");
    }
  }, [workflowId]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (workflowStatus !== "RUNNING") return;
    const intervalId = setInterval(refreshStatus, 1000);
    return () => clearInterval(intervalId);
  }, [refreshStatus, workflowStatus]);

  useEffect(() => {
    if (!startedHere || !workflowStatus || workflowStatus === "RUNNING") {
      return;
    }
    if (workflowStatus === "COMPLETED") {
      showNotification("Lecture re-uploaded to the hotline.", {
        type: "success",
      });
    } else if (failedWorkflowStatuses.includes(workflowStatus)) {
      showNotification("Hotline re-upload failed. Please try again.", {
        type: "error",
      });
    }
    setStartedHere(false);
  }, [showNotification, startedHere, workflowStatus]);

  const handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (
      !window.confirm(
        "This will delete and recreate this lecture's hotline entry. Continue?",
      )
    ) {
      return;
    }

    setIsStarting(true);
    setStatusError("");
    try {
      await authClient.post("/temporal/workflows/LectureVoitexUploading", {
        taskQueue: "general",
        workflowId,
        args: [{ lectureId: lecture.id }],
      });
      setStartedHere(true);
      setWorkflow({ status: { name: "RUNNING" } });
      showNotification("Hotline re-upload started.", { type: "info" });
    } catch (error) {
      showNotification(error.body?.message || error.message, {
        type: "error",
      });
    } finally {
      setIsStarting(false);
    }
  };

  const completedAt = workflow?.closeTime
    ? new Date(workflow.closeTime).toLocaleString()
    : null;
  let statusText = "";
  if (isRunning) {
    statusText = "Re-uploading to the hotline...";
  } else if (workflowStatus === "COMPLETED") {
    statusText = `Last re-upload completed${completedAt ? ` ${completedAt}` : ""}.`;
  } else if (failedWorkflowStatuses.includes(workflowStatus)) {
    statusText = `Last re-upload failed${completedAt ? ` ${completedAt}` : ""}.`;
  } else if (!lecture?.mp3_url) {
    statusText = "An MP3 is required before re-uploading.";
  }

  return (
    <div style={{ display: "inline-flex", alignItems: "center" }}>
      <Button onClick={handleClick} disabled={isRunning || !lecture?.mp3_url}>
        Re-Upload to Hotline
      </Button>
      {(statusText || statusError) && (
        <span
          style={{
            color: statusError ? "#d32f2f" : "inherit",
            marginLeft: 8,
          }}
        >
          {statusError || statusText}
        </span>
      )}
    </div>
  );
};

/**
 *
 * @param {Object} params
 * @param {number} params.lectureId - lecture id
 * @param {number} params.duration - lecture duration
 * @returns
 */
const UpdateThumbnail = (params) => {
  const { lectureId, duration } = params;

  const [thumbnailUpdateSeconds, setThumbnailUpdateSeconds] = useState(null);
  const [updatingThumbnail, setUpdatingThumbnail] = useState(false);

  const error = thumbnailUpdateSeconds > duration;
  return (
    <div>
      <MaterialTextField
        style={{
          width: 200,
        }}
        type="number"
        placeholder="seconds"
        onChange={(event) => setThumbnailUpdateSeconds(event.target.value)}
        value={thumbnailUpdateSeconds || ""}
        error={error}
        helperText={
          error ? "Can't be greater than duration of lecture" : undefined
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setThumbnailUpdateSeconds(
                    Math.floor(Math.random() * duration) + 1,
                  );
                }}
              >
                <Shuffle />
              </Button>
            </InputAdornment>
          ),
        }}
      />
      <Button
        color="primary"
        variant="text"
        disabled={!thumbnailUpdateSeconds || updatingThumbnail}
        onClick={async () => {
          setUpdatingThumbnail(true);
          await authClient.post("/temporal/workflows/LectureThumbnailAtTime", {
            taskQueue: "transcoder",
            workflowId: `LectureThumbnailAtTime-${lectureId}`,
            args: [
              {
                lectureId,
                timeIn: Number.parseInt(thumbnailUpdateSeconds),
              },
            ],
          });

          // eslint-disable-next-line no-restricted-globals
          location.reload();
          // Instead of this, we should figure out how to inject
          // the new thumbnail id into React Admin state.
        }}
      >
        Save
      </Button>
    </div>
  );
};

function GenerateWatermarkedOriginalMp4Button() {
  const lecture = useRecordContext();
  const { workflow, isLoading, startWorkflow, refresh } = useTemporalWorkflow(
    "WatermarkedOriginal",
    `WatermarkedOriginal-${lecture.id}`,
  );

  const handleClick = useCallback(() => {
    startWorkflow("transcoder", [
      {
        lectureId: lecture.id,
        slug: lecture.slug?.substring(0, 64) || "",
      },
    ]);
  }, [lecture, startWorkflow]);

  const RefreshButton = () => (
    <IconButton onClick={refresh}>
      <Refresh />
    </IconButton>
  );

  if (isLoading) {
    return (
      <div>
        <span>Loading... </span>
        <RefreshButton />
        {/* <TemporalProgressField
          workflowId={`TemporaryVideoClip-${lecture.id}`}
          activityNames={["downloadFileById"]}
          pollInterval={1000}
        /> */}
      </div>
    );
  }
  if (
    workflow?.status?.name === "RUNNING" &&
    workflow?.raw.pendingActivities?.[0].activityType.name === "ffmpeg"
  ) {
    return (
      <div>
        <span>
          {(
            (workflow?.raw.pendingActivities?.[0].heartbeat?.progressRatio ||
              0) * 100
          ).toFixed(2)}
          %{" "}
        </span>
        <RefreshButton />
      </div>
    );
  }
  if (workflow?.status?.name !== "COMPLETED" && workflow !== null) {
    return (
      <div>
        <span>{workflow?.status?.name} </span>
        <RefreshButton />
      </div>
    );
  }
  if (
    lecture.watermarked_original_mp4_url === "" ||
    lecture.watermarked_original_mp4_url === null
  ) {
    return (
      <Button variant="outlined" onClick={handleClick}>
        Generate Watermarked Original
      </Button>
    );
  }
  return <></>;
}

function CreateView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Create
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: (lecture) => {
          // Resolve an uploader-initiated save before navigating away so its
          // selected file can be attached to the newly assigned lecture ID.
          MediaUploader.lectureSaveSucceeded(lecture.id);
          notify("Lecture created successfully!", {
            type: "success",
          });
          refresh(); // Ensures UI updates with the new data
          redirect("list", "lectures");
        },
        onError: (error) => {
          MediaUploader.lectureSaveFailed(error);
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      <TabbedForm
        defaultValue={defaultValue}
        toolbar={
          <EditorToolbar
            registerTrigger={MediaUploader.setLectureActions}
            showSave={authUser.isAdministrator}
          />
        }
        submitOnEnter={false}
      >
        <FormTab label="Details" replace>
          <TextInput source="title" validate={validate.title} autoFocus />
          <DateInput source="date_recorded" validate={validate.date_recorded} />

          <LanguageInput validate={validate.language} />
          <LocationInput validate={validate.location} />
          <HostInput />
          <OrganizationInput />
          <SpeakerInput validate={validate.speaker} />
          <CategoryInput validate={validate.category} />
          <SubCategoryInput />
          <EventSpeakersInput />

          <BooleanInput
            source="on_recent_list"
            label="Show on 'Recent Lectures' on Home Page"
            defaultValue={1}
          />
          <BooleanInput
            source="auto_display"
            label="Auto Send Email and Publish When Encoding Completes"
            defaultValue={1}
          />
          {/* These independent flags request confirmation on website lecture pages only. */}
          <BooleanInput
            source="is_sensitive_for_young"
            label="Sensitive Content: 18+ Confirmation"
            defaultValue={false}
          />
          <BooleanInput
            source="is_sensitive_for_unmarried"
            label="Sensitive Content: Married Confirmation"
            defaultValue={false}
          />
          <BooleanInput source="no_download" label="Disable downloads" />
          <BooleanInput
            source="ladies"
            label="Only Ladies can View After Login"
          />
        </FormTab>
      </TabbedForm>
    </Create>
  );
}

function EditView(props) {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  // const {
  //   location: { pathname = "" },
  //   id,
  // } = props;
  // console.log({lectureprops:props});
  const [onRethink, setOnRethink] = useState(false);
  useEffect(() => {
    const tabIndex = window.location.hash.split("/").pop();
    if (tabIndex === "/2") {
      setOnRethink(true);
    } else {
      setOnRethink(false);
    }
  }, [window.location.hash]);

  // return <EditGuesser {...props} />;
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: (lecture) => {
          // Normal saves are ignored; this only resolves a pending request
          // made by the embedded uploader.
          MediaUploader.lectureSaveSucceeded(lecture.id);
          notify("Lecture updated successfully!", {
            type: "success",
          });
          refresh(); // Ensures UI updates with the new data
          redirect(false);
        },
        onError: (error) => {
          MediaUploader.lectureSaveFailed(error);
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      <TabbedForm
        toolbar={
          onRethink ? (
            <div></div>
          ) : (
            <EditorToolbar
              registerTrigger={MediaUploader.setLectureActions}
              handleSubmitWithRedirect={(redirect) => () => {}}
            >
              <SendNotificationsButton toolbarProps />
              {authUser.isAdministrator && <ReUploadToHotlineButton />}
              <DeleteWithConfirmButton />
            </EditorToolbar>
          )
        }
        submitOnEnter={false}
      >
        <FormTab label="Details" replace>
          {/* Independent flags retain overlapping and empty memberships. */}
          <BooleanInput source="is_torahanytime" label="TorahAnytime" />
          <BooleanInput source="is_jewishanytime" label="JewishAnytime" />
          <BooleanInput source="is_rebbeanytime" label="RebbeAnytime" />
          <h6 style={styles.label}>Website</h6>
          <UrlField
            source="url"
            label="Website"
            target="_blank"
            rel="noopener noreferrer"
          />
          <h6 style={styles.label}>Video Files</h6>
          <UrlField
            source="mp4_url"
            label="Video File"
            target="_blank"
            rel="noopener noreferrer"
          />
          <UrlField
            source="m3u8_url"
            label="Video File (HLS m3u8)"
            target="_blank"
            rel="noopener noreferrer"
          />
          <UrlField
            source="watermarked_original_mp4_url"
            label="Watermarked Original"
            target="_blank"
            rel="noopener noreferrer"
          />
          <GenerateWatermarkedOriginalMp4Button />
          <h6 style={styles.label}>Audio File</h6>
          <UrlField
            source="mp3_url"
            label="Audio File"
            target="_blank"
            rel="noopener noreferrer"
          />
          <h6 style={styles.label}>Video Shortlink</h6>
          <FunctionField
            source="bitly_video"
            label="Video Shortlink"
            style={{ width: "inherit" }}
            render={(record) => {
              return (
                <div style={{ display: "flex" }}>
                  <UrlField
                    record={record}
                    source="bitly_video"
                    label="Video Shortlink"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                  <CopyToClipboard textToCopy={record.bitly_video} />
                </div>
              );
            }}
          />
          <h6 style={styles.label}>Audio Shortlink</h6>
          <FunctionField
            source="bitly_audio"
            label="Audio Shortlink"
            style={{ width: "inherit" }}
            render={(record) => {
              return (
                <div style={{ display: "flex" }}>
                  <UrlField
                    record={record}
                    source="bitly_audio"
                    label="Audio Shortlink"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                  <CopyToClipboard textToCopy={record.bitly_video} />
                </div>
              );
            }}
          />

          <SanitizedP>Originals are stored in box.</SanitizedP>
          <h6 style={styles.label}>Box</h6>
          <UrlField
            source="box_url"
            label="Box"
            target="_blank"
            rel="noopener noreferrer"
          />

          <TextInput source="thumbnail_url" fullWidth />
          <ImageField source="thumbnail_url" label="Thumbnail" />
          <FunctionField
            label="Change Thumbnail"
            render={(record) => (
              <UpdateThumbnail
                lectureId={record.id}
                duration={record.duration}
              />
            )}
          />
          <ImageInput
            source="FILE_thumbnail"
            label="Thumbnail Upload"
            accept="image/*"
            placeholder={<p>Click or drop a photo here.</p>}
            touch="true"
          >
            <ImageField source="src" title="title" />
          </ImageInput>

          <BooleanInput source="display_active" label="Display" />
          <BooleanInput
            source="auto_display"
            label="Auto Send Email and Publish When Encoding Completes"
          />
          <TextInput source="title" validate={validate.title} fullWidth />
          <BooleanInput source="title_rtl" label="Title Right-to-Left" />
          <NumberInput source="duration" label="Duration (in seconds)" />
          <DateTimeInput source="date_created" />
          <DateInput source="date_recorded" validate={validate.date_recorded} />
          <DateTimeInput source="date_to_show" />
          <DateTimeInput source="publish_by_date" />
          <LanguageInput validate={validate.language} />
          <LocationInput validate={validate.location} />
          <HostInput />
          <OrganizationInput fullWidth />
          <SpeakerInput validate={validate.speaker} fullWidth />
          <CategoryInput validate={validate.category} fullWidth />
          <SubCategoryInput fullWidth />
          <EventSpeakersInput />
          <BooleanInput
            source="on_recent_list"
            label="Show on 'Recent Lectures' on Home Page"
          />
          <BooleanInput source="is_short" label="Is Short?" />
          <BooleanInput source="no_download" label="Disable downloads" />
          <BooleanInput source="private" label="Private Lecture" />
          <TextInput source="password" label="Password" />
          <TextInput source="dedication" label="Dedication message" fullWidth />
          <TextInput source="desc" label="Description" fullWidth multiline />
          {/* <RichTextInput
            width={"100%"}
            source="desc"
            toolbar={
              <RichTextInputToolbar>
                <FormatButtons size="large" />
                <ListButtons size={"large"} />
                <LinkButtons size={"large"} />
              </RichTextInputToolbar>
            }
          /> */}
        </FormTab>
        <FormTab label="Access Restriction Flags" replace>
          {/* These independent flags request confirmation on website lecture pages only. */}
          <BooleanInput
            source="is_sensitive_for_young"
            label="Sensitive Content: 18+ Confirmation"
            defaultValue={false}
          />
          <BooleanInput
            source="is_sensitive_for_unmarried"
            label="Sensitive Content: Married Confirmation"
            defaultValue={false}
          />
          <BooleanInput source="display_active" label="Display" />
          {(permissions?.includes("thumbnail_admin") || permissions?.includes("administrator")) && (
            <BooleanInput
              source="is_thumbnail_only_viewable_by_female"
              label="Thumbnail Female-Only"
              helperText="To approve a public thumbnail, upload the custom image in this save. Does not change discovery or playback."
            />
          )}
          <BooleanInput
            source="is_only_watchable_by_female"
            label="Only Watchable by Female"
          />
          <BooleanInput
            source="is_only_listenable_by_female"
            label="Only Listenable by Female"
          />
          <BooleanInput
            source="is_only_discoverable_by_female"
            label="Only Discoverable by Female"
          />
          <BooleanInput
            source="is_only_discoverable_within_org"
            label="Only Discoverable within Org"
          />
          <BooleanInput source="is_not_discoverable" label="Not Discoverable" />
          <BooleanInput
            source="is_from_shabbaton_2026"
            label="Shabbaton 2026 Exclusive"
          />
          <BooleanInput
            source="is_torahanytime_plus"
            label="TorahAnytime Plus Exclusive"
          />
        </FormTab>
        {/* <FormTab label="Rethink Database" replace>
          <LectureRethink />
        </FormTab> */}
        <FormTab label="JewishAnytime" replace>
          <FunctionField
            label="JewishAnytime Thumbnail (this may take time to update)"
            render={(record) => (
              <>
                <img
                  src={`https://torahanytime-files.sfo2.cdn.digitaloceanspaces.com/assets/thumbnails/${record.id}.jpg`}
                  width={200}
                  title="ja thumnail"
                  alt="JewishAnytime Thumbnail"
                />
                <ImageInput
                  source="FILE_photo"
                  label="JewishAnytime Thumbnail Upload "
                  accept="image/*"
                  placeholder={
                    <p>
                      Click or drop a photo here. <br />
                      Size: 1280x720
                      <br />
                      (Youtube Thumbnail size)
                    </p>
                  }
                  touch="true"
                >
                  <ImageField source="src" title="title" />
                </ImageInput>
              </>
            )}
          />
          <FunctionField
            label="Feature on JewishAnytime"
            render={(record) =>
              record.is_jewishanytime ? (
                <>
                  <BooleanInput source="is_featured_on_JA" label="Featured" />
                  <TextInput source="feature_caption" label="Featured caption" />
                  <img
                    src={record.featured_banner}
                    width={600}
                    title="featured banner"
                    alt="Featured Banner"
                  />
                  <ImageInput
                    source="FILE_banner"
                    label="Upload Banner"
                    accept="image/*"
                    placeholder={<p>Click or drop a full-size banner here.</p>}
                    touch="true"
                  >
                    <ImageField source="src" title="title" />
                  </ImageInput>
                </>
              ) : (
                <label>
                  Only JewishAnytime lectures can be featured on JewishAnytime
                </label>
              )
            }
          />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
}

const filters = [
  <RadioButtonGroupInput
    alwaysOn
    label="Display"
    source="display_active"
    initialValue=""
    options={{
      row: true,
    }}
    choices={[
      { id: "", name: "All" },
      { id: "1", name: "Active" },
      { id: "0", name: "Inactive" },
    ]}
  />,

  <TextInput source="id" alwaysOn />,
  <TextInput source="title" alwaysOn />,
  <SpeakerInput
    label="Speaker"
    source="speaker"
    reference="speakers"
    alwaysOn
  />,
  <BooleanInput
    alwaysOn
    source="is_short"
    label="Shorts Only"
    sx={{
      "&": {
        paddingY: "10px",
        paddingLeft: "10px",
        backgroundColor: "#f5f5f5",
        height: "50px",
      },
    }}
  />,
  <OrganizationInput
    label="Organization"
    source="org"
    reference="organizations"
  />,
  <CategoryInput label="Category" source="category" reference="categories" />,
  <SubCategoryInput
    label="Sub-Category"
    source="subcategory"
    reference="categories"
  />,
  <TextInput source="published_by_email" label="Published by" />,
];

function ListLayout(props) {
  if (props.infinite)
    return (
      <InfiniteList
        {...props}
        actions={<CardListActions exporter={true} />}
        filters={filters}
        filterDefaultValues={{ display_active: "" }}
        debounce={1000}
        perPage={50}
        sort={{
          field: "id",
          order: "DESC",
        }}
        title="Lectures"
      >
        {props.children}
      </InfiniteList>
    );

  return (
    <List
      {...props}
      actions={<CardListActions exporter={true} />}
      filters={filters}
      filterDefaultValues={{ display_active: "" }}
      debounce={1000}
      pagination={<MorePagination />}
      sort={{
        field: "id",
        order: "DESC",
      }}
      title="Lectures"
    >
      {props.children}
    </List>
  );
}

function ListView(props) {
  const [infinite, setInfinite] = useState(false);
  const { permissions } = usePermissions();
  console.log(permissions);
  return (
    <div>
      {/* <Switch value={infinite} onChange={setInfinite} /> For meir- toggle on infinite list mode*/}

      <ListLayout
        {...props}
        actions={<CardListActions exporter={true} />}
        filters={filters}
        filterDefaultValues={{ display_active: "" }}
        perPage={permissions?.includes("super_admin") ? 250 : 25}
        pagination={<MorePagination />}
        sort={{
          field: "id",
          order: "DESC",
        }}
        title="Lectures"
        infinite={infinite}
      >
        <Datagrid rowClick="edit" bulkActionButtons={false}>
          <TextField source="id" />
          <ImageField
            source="thumbnail_url"
            label="Thumbnail"
            sx={{
              "& img": { maxWidth: 135, maxHeight: 76, objectFit: "contain" },
            }}
          />
          <TextField source="speaker_name" label="Speaker" />
          <TextField source="title" />
          <BooleanField source="display_active" label="Active"></BooleanField>
          <DateTimeField
            source="created_at"
            label="Published at"
            style={{ minWidth: 150 }}
          />
          <DateTimeField
            source="updated_at"
            label="Updated at"
            style={{ minWidth: 150 }}
          />
          <TextField source="published_by_email" label="Published by" />
        </Datagrid>
      </ListLayout>
    </div>
  );
}

export const Lectures = {
  category: "database",
  name: "lectures",
  icon: PlayCircle,
  list: ListView,
  options: { label: "Lectures" },
  edit: EditView,
  create: CreateView,
  roles: ["author"],
  permissions: {
    author: ["create"],
  },
};
